/**
 * DOM safe-area 어댑터 — `SafeAreaSource` Port의 구현(DIP 어댑터, 앱 소유).
 *
 * `--dvp-safe-*`(device-preview 시뮬레이터가 :root에 주입) 우선, 없으면 `env(safe-area-inset-*)`
 * (실기기 — probe 엘리먼트의 padding으로 측정)로 CSS px inset을 읽어 world 단위로 환산해 반환한다.
 * 웹·네이티브 공통 경로 — GameScene.create가 read()로 디바이스 inset을 읽어 레이아웃을 압축한다.
 *
 * jsdom은 getComputedStyle의 env()/CSS 변수를 해석하지 않으므로 단위테스트 비용 큼(실기기 검증 영역).
 * 순수 환산 로직은 @yjsgames/safe-area(resolveWorldInsets)가 cover한다.
 */
import { Capacitor } from "@capacitor/core";
import { resolveWorldInsets, type SafeAreaInsets, type SafeAreaSource } from "@yjsgames/safe-area";
import { DESIGN } from "../data/balance.js";

export function createDomSafeAreaSource(rootId = "game-root"): SafeAreaSource {
  return {
    read(): SafeAreaInsets {
      const root = document.getElementById(rootId);
      // canvas FIT 기준 화면 = #game-root 레이아웃 px(transform 면역). 미마운트면 window로 폴백.
      const screen =
        root && root.clientWidth > 0 && root.clientHeight > 0
          ? { width: root.clientWidth, height: root.clientHeight }
          : { width: window.innerWidth, height: window.innerHeight };
      const css = readCssInsets(screen);
      return resolveWorldInsets(screen, DESIGN, css);
    },
  };
}

/** `--dvp-safe-*`(시뮬레이터) 우선 — 하나라도 숫자면 사용, 전부 없으면 env() probe(실기기). */
function readCssInsets(screen: { width: number; height: number }): SafeAreaInsets {
  const root = getComputedStyle(document.documentElement);
  const dvp = (side: string): number => parseFloat(root.getPropertyValue(`--dvp-safe-${side}`));
  const top = dvp("top");
  const right = dvp("right");
  const bottom = dvp("bottom");
  const left = dvp("left");
  if ([top, right, bottom, left].some((v) => !Number.isNaN(v))) {
    return { top: top || 0, right: right || 0, bottom: bottom || 0, left: left || 0 };
  }
  const env = readEnvInsets();
  const fallback =
    Capacitor.getPlatform() === "ios" && isZeroInsets(env)
      ? estimateIosSafeAreaCssInsets(screen, navigator.userAgent)
      : null;
  if (fallback) {
    return fallback;
  }
  return env;
}

/** env(safe-area-inset-*)를 hidden probe의 padding으로 측정(실기기 — 데스크톱/미지원 환경은 0). */
function readEnvInsets(): SafeAreaInsets {
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;top:0;left:0;width:0;height:0;" +
    "padding-top:env(safe-area-inset-top);padding-right:env(safe-area-inset-right);" +
    "padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left);";
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  const insets: SafeAreaInsets = {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };
  probe.remove();
  return insets;
}

export function estimateIosSafeAreaCssInsets(
  screen: { width: number; height: number },
  userAgent: string,
): SafeAreaInsets | null {
  if (!/iPhone|iPad|iPod/.test(userAgent)) return null;
  const short = Math.min(screen.width, screen.height);
  const long = Math.max(screen.width, screen.height);
  const portrait = screen.height >= screen.width;
  if (short >= 390 && short <= 430 && long >= 830 && long <= 940) {
    return portrait
      ? { top: 59, right: 0, bottom: 34, left: 0 }
      : { top: 0, right: 59, bottom: 21, left: 59 };
  }
  if (short >= 375 && short < 390 && long >= 800 && long <= 900) {
    return portrait
      ? { top: 47, right: 0, bottom: 34, left: 0 }
      : { top: 0, right: 47, bottom: 21, left: 47 };
  }
  return null;
}

function isZeroInsets(insets: SafeAreaInsets): boolean {
  return insets.top === 0 && insets.right === 0 && insets.bottom === 0 && insets.left === 0;
}
